import React, { ErrorInfo, ReactNode } from 'react';
import { AlertCircle, RefreshCw, ShieldAlert, Database, Lock } from 'lucide-react';
import { isStaleChunkLoadError, refreshApplicationAfterUpdate } from '@/lib/appRefresh';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  errorMessage: string;
  isStaleChunkError: boolean;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      errorMessage: '',
      isStaleChunkError: false,
    };
  }

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      errorMessage: error.message,
      isStaleChunkError: isStaleChunkLoadError(error),
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
    
    // Log to console with structured data for debugging
    const errorLog = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    };
    
    // Send to any logging service (Sentry, LogRocket, etc.)
    // Example: Sentry.captureException(error, { extra: errorInfo });
    
    // Store in localStorage for later retrieval
    const logs = JSON.parse(localStorage.getItem('error_logs') || '[]');
    logs.unshift(errorLog);
    localStorage.setItem('error_logs', JSON.stringify(logs.slice(0, 10))); // Keep last 10
  }

  private getOperationName(op: string) {
    const ops: Record<string, string> = {
      'create': 'Tworzenie danych',
      'update': 'Aktualizacja danych',
      'delete': 'Usuwanie danych',
      'list': 'Pobieranie listy',
      'get': 'Pobieranie dokumentu',
      'write': 'Zapisywanie'
    };
    return ops[op] || op;
  }

  private getFriendlyErrorMessage(error: string) {
    if (error.includes('Missing or insufficient permissions')) {
      return 'Brak wystarczających uprawnień do wykonania tej akcji. Upewnij się, że jesteś zalogowany na właściwe konto lub skontaktuj się z administratorem.';
    }
    if (error.includes('the client is offline')) {
      return 'Wygląda na to, że jesteś offline. Sprawdź połączenie z internetem.';
    }
    if (error.includes('Quota exceeded')) {
      return 'Przekroczono limit darmowych operacji bazy danych. Spróbuj ponownie jutro.';
    }
    return error;
  }

  public render() {
    if (this.state.hasError) {
      let parsedError: any = null;
      try {
        parsedError = JSON.parse(this.state.errorMessage);
      } catch (e) {
        // Not a JSON error
      }

      const isPermissionError = parsedError?.error?.includes('Missing or insufficient permissions');
      const isStaleChunkError = this.state.isStaleChunkError || isStaleChunkLoadError(this.state.errorMessage);

      return (
        <div className="min-h-[400px] flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            <div className={`${isStaleChunkError ? 'bg-amber-500' : 'bg-red-600'} p-6 flex justify-center`}>
              {isPermissionError ? (
                <Lock className="w-12 h-12 text-white" />
              ) : isStaleChunkError ? (
                <RefreshCw className="w-12 h-12 text-white" />
              ) : (
                <AlertCircle className="w-12 h-12 text-white" />
              )}
            </div>
            
            <div className="p-8 space-y-6">
              <div className="text-center">
                <h2 className="text-xl font-bold text-slate-900">
                  {isStaleChunkError
                    ? 'Aplikacja została zaktualizowana'
                    : isPermissionError
                      ? 'Błąd uprawnień'
                      : 'Wystąpił nieoczekiwany błąd'}
                </h2>
                <p className="text-slate-500 mt-2 text-sm">
                  {isStaleChunkError
                    ? 'Odśwież aplikację, aby pobrać nową wersję.'
                    : 'Przepraszamy za utrudnienia. Poniżej znajdują się szczegóły techniczne:'}
                </p>
              </div>

              {isStaleChunkError ? (
                <div className="bg-amber-50 p-4 rounded-xl border border-amber-100">
                  <p className="text-sm text-amber-900">
                    To zwykle dzieje się po aktualizacji aplikacji na telefonie. Odświeżenie wyczyści stare pliki i pobierze aktualną wersję.
                  </p>
                </div>
              ) : parsedError ? (
                <div className="bg-slate-50 rounded-xl p-4 space-y-3 border border-slate-100">
                  <div className="flex items-start gap-3">
                    <Database className="w-4 h-4 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Operacja</p>
                      <p className="text-sm font-medium text-slate-700">{this.getOperationName(parsedError.operationType)}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <ShieldAlert className="w-4 h-4 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Komunikat</p>
                      <p className="text-sm text-slate-600 leading-relaxed">
                        {this.getFriendlyErrorMessage(parsedError.error)}
                      </p>
                    </div>
                  </div>

                  {parsedError.path && (
                    <div className="pt-2 border-t border-slate-200">
                      <p className="text-[10px] text-slate-400">Lokalizacja: <span className="font-mono">{parsedError.path}</span></p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-red-50 p-4 rounded-xl border border-red-100">
                  <p className="text-sm text-red-800 font-mono break-words">{this.state.errorMessage}</p>
                </div>
              )}

              <div className="flex flex-col gap-3">
                <button 
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-xl font-semibold hover:bg-slate-800 transition-colors shadow-lg shadow-slate-200"
                  onClick={() => {
                    if (isStaleChunkError) {
                      void refreshApplicationAfterUpdate();
                      return;
                    }
                    window.location.reload();
                  }}
                >
                  <RefreshCw className="w-4 h-4" />
                  Odśwież aplikację
                </button>
                {!isStaleChunkError && (
                  <button
                    className="w-full px-6 py-3 text-slate-500 text-sm font-medium hover:text-slate-700 transition-colors"
                    onClick={() => this.setState({ hasError: false, errorMessage: '', isStaleChunkError: false })}
                  >
                    Spróbuj kontynuować
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
